import { z } from "zod";

import {
  CUSTOM_EQUIPMENT_PREFIX,
  EQUIPMENT,
  EQUIPMENT_KEYS,
  EquipmentCategory,
  type EquipmentKey,
  EquipmentLoadKind,
  GymKind,
  WeightUnit,
} from "./training";

/** One equipment item as a gym stores it. Loads are kilograms; see `GymEquipment` in gym.prisma. */
export const gymEquipmentSchema = z.object({
  equipmentKey: z.union([z.enum(EQUIPMENT_KEYS), z.string().startsWith(CUSTOM_EQUIPMENT_PREFIX).max(64)]),
  name: z.string().min(1).max(120).nullable().default(null),
  category: z.enum(EquipmentCategory).nullable().default(null),
  weightsKg: z.array(z.number().positive().max(500)).max(80).default([]),
  barKg: z.number().min(0).max(100).nullable().default(null),
  stackMinKg: z.number().min(0).max(500).nullable().default(null),
  stackMaxKg: z.number().min(0).max(500).nullable().default(null),
  stackStepKg: z.number().positive().max(50).nullable().default(null),
});
export type GymEquipmentInput = z.infer<typeof gymEquipmentSchema>;

/** A gym from OpenStreetMap, as the search returned it, picked for the first time. */
export const osmGymSchema = z.object({
  ref: z.string().regex(/^osm:(node|way|relation)\/\d+$/),
  name: z.string().min(1).max(160),
  address: z.string().max(300).default(""),
  city: z.string().max(120).default(""),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

/**
 * Which real place a new gym profile is of: one already in our directory, or one the search found
 * on OpenStreetMap. `custom` is a place not in the directory: the gym stays private to its owner,
 * and the address only matters when they also suggest it to the directory. A home gym has none.
 */
export const gymPlaceChoiceSchema = z.union([
  z.object({ placeId: z.uuid() }),
  z.object({ osm: osmGymSchema }),
  z.object({ custom: z.object({ address: z.string().max(300).default(""), city: z.string().max(120).default("") }) }),
]);
export type GymPlaceChoice = z.infer<typeof gymPlaceChoiceSchema>;

export const createGymSchema = z.object({
  name: z.string().trim().min(1).max(120),
  kind: z.enum(GymKind),
  weightUnit: z.enum(WeightUnit).default(WeightUnit.KG),
  place: gymPlaceChoiceSchema.optional(),
  /** With a `custom` place: also ask admins to add it to the directory. */
  suggestToDirectory: z.boolean().default(false),
});

/** Where a directory place stands. A closed place still shows on its athletes' gyms; a removed one is gone. */
export enum GymPlaceStatus {
  ACTIVE = "active",
  CLOSED = "closed",
  REMOVED = "removed",
}

/** Where a directory place came from. */
export enum GymPlaceSource {
  OSM = "osm",
  ADMIN = "admin",
}

export const WEEKDAYS = ["mo", "tu", "we", "th", "fr", "sa", "su"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/);

/** One stretch a place is open, "06:00"–"22:00". A stretch past midnight ends at "24:00". */
export const openingRangeSchema = z
  .tuple([clockTime, clockTime])
  .refine(([from, to]) => from < to, "Opens before it closes");

const openDay = z.array(openingRangeSchema).max(4);

/** A place's week: each day's open stretches, an empty day is closed. */
export const openingHoursSchema = z.object({
  mo: openDay,
  tu: openDay,
  we: openDay,
  th: openDay,
  fr: openDay,
  sa: openDay,
  su: openDay,
});
export type OpeningHours = z.infer<typeof openingHoursSchema>;

/** What someone can ask admins to change in the directory. */
export enum GymReportKind {
  CLOSED = "closed",
  NOT_EXISTS = "not_exists",
  OPENING_HOURS = "opening_hours",
  // oxlint-disable-next-line no-shadow -- an enum member, only ever read as GymReportKind.EQUIPMENT
  EQUIPMENT = "equipment",
  MISSING_PLACE = "missing_place",
  /** A listed place's name or address is wrong. */
  DETAILS = "details",
}

export enum GymReportStatus {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
}

/** What coaches at a place may propose; athletes may report every kind. */
export const COACH_GYM_REPORT_KINDS = [GymReportKind.EQUIPMENT, GymReportKind.OPENING_HOURS] as const;

const reportNote = z.string().trim().max(500).default("");

/**
 * A gym report as it is filed: its `kind` and that kind's payload. `equipment` is the whole list
 * the place should have, not a diff, so approving it is a plain replace.
 */
export const gymReportSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal(GymReportKind.CLOSED), note: reportNote }),
  z.object({ kind: z.literal(GymReportKind.NOT_EXISTS), note: reportNote }),
  z.object({ kind: z.literal(GymReportKind.OPENING_HOURS), openingHours: openingHoursSchema, note: reportNote }),
  z.object({
    kind: z.literal(GymReportKind.EQUIPMENT),
    equipment: z.array(gymEquipmentSchema).max(200),
    note: reportNote,
  }),
  z.object({
    kind: z.literal(GymReportKind.MISSING_PLACE),
    name: z.string().trim().min(1).max(160),
    address: z.string().trim().max(300).default(""),
    city: z.string().trim().min(1).max(120),
    note: reportNote,
  }),
  z.object({
    kind: z.literal(GymReportKind.DETAILS),
    name: z.string().trim().min(1).max(160),
    address: z.string().trim().max(300).default(""),
    city: z.string().trim().max(120).default(""),
    note: reportNote,
  }),
]);
export type GymReportInput = z.input<typeof gymReportSchema>;
export type GymReport = z.infer<typeof gymReportSchema>;

/** A directory place as an admin writes it. */
export const gymPlaceSchema = z.object({
  name: z.string().trim().min(1).max(160),
  address: z.string().trim().max(300).default(""),
  city: z.string().trim().max(120).default(""),
  countryCode: z.string().length(2).default("PL"),
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
  status: z.enum(GymPlaceStatus).default(GymPlaceStatus.ACTIVE),
  openingHours: openingHoursSchema.nullable().default(null),
  website: z.string().trim().max(300).default(""),
});

const sorted = (weights: number[]) => weights.toSorted((a, b) => a - b).join(",");

/** Whether two items of one key would load the same. */
const sameLoads = (a: GymEquipmentInput, b: GymEquipmentInput) =>
  a.name === b.name &&
  a.category === b.category &&
  a.barKg === b.barKg &&
  a.stackMinKg === b.stackMinKg &&
  a.stackMaxKg === b.stackMaxKg &&
  a.stackStepKg === b.stackStepKg &&
  sorted(a.weightsKg) === sorted(b.weightsKg);

/** What turning `before` into `after` adds, removes and changes, by equipment key. */
export const diffEquipment = (before: GymEquipmentInput[], after: GymEquipmentInput[]) => {
  const was = new Map(before.map((item) => [item.equipmentKey, item]));
  const now = new Map(after.map((item) => [item.equipmentKey, item]));
  return {
    added: after.filter((item) => !was.has(item.equipmentKey)).map((item) => item.equipmentKey),
    removed: before.filter((item) => !now.has(item.equipmentKey)).map((item) => item.equipmentKey),
    changed: after.flatMap((item) => {
      const old = was.get(item.equipmentKey);
      return old && !sameLoads(old, item) ? [item.equipmentKey] : [];
    }),
  };
};
export type EquipmentDiff = ReturnType<typeof diffEquipment>;

export const updateGymSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  weightUnit: z.enum(WeightUnit).optional(),
});

const steps = (from: number, to: number, step: number) =>
  Array.from(
    { length: Math.floor((to - from) / step + 1e-9) + 1 },
    (_, index) => Math.round((from + index * step) * 100) / 100,
  );

const PLATES_KG = [1.25, 2.5, 5, 10, 15, 20, 25];

/** What a plate-loaded item weighs empty: the bar, the Smith carriage, the leg-press sled. */
const EMPTY_BAR_KG: Partial<Record<EquipmentKey, number>> = {
  barbell: 20,
  ez_bar: 10,
  trap_bar: 25,
  smith_machine: 15,
  leg_press: 50,
  hack_squat: 40,
};

/** Loads an item starts with when a gym adds it, before anyone edits them. */
export const defaultEquipmentLoads = (key: EquipmentKey, kind: GymKind): GymEquipmentInput => {
  const base: GymEquipmentInput = {
    equipmentKey: key,
    name: null,
    category: null,
    weightsKg: [],
    barKg: null,
    stackMinKg: null,
    stackMaxKg: null,
    stackStepKg: null,
  };

  switch (EQUIPMENT[key].load) {
    case EquipmentLoadKind.FIXED_WEIGHTS: {
      const heaviest = { [GymKind.LARGE]: 50, [GymKind.SMALL]: 30, [GymKind.GARAGE]: 25 }[kind];
      if (key === "kettlebells")
        return { ...base, weightsKg: [8, 12, 16, 20, 24, 28, 32].filter((kg) => kg <= heaviest + 8) };
      if (key === "medicine_ball") return { ...base, weightsKg: [3, 5, 7, 9] };
      return { ...base, weightsKg: [1, 2, ...steps(2.5, heaviest, 2.5)] };
    }
    case EquipmentLoadKind.PLATES: {
      return {
        ...base,
        barKg: EMPTY_BAR_KG[key] ?? 20,
        weightsKg: kind === GymKind.GARAGE ? [1.25, 2.5, 5, 10, 20] : PLATES_KG,
      };
    }
    case EquipmentLoadKind.STACK:
      return {
        ...base,
        stackMinKg: key === "assisted_pull_up" ? 5 : 2.5,
        stackMaxKg: key === "cable_station" ? 50 : 100,
        stackStepKg: key === "cable_station" ? 2.5 : 5,
      };
    default:
      return base;
  }
};

const GARAGE: EquipmentKey[] = [
  "dumbbells",
  "kettlebells",
  "barbell",
  "plates",
  "flat_bench",
  "adjustable_bench",
  "squat_rack",
  "pull_up_bar",
  "resistance_bands",
  "ab_wheel",
];

const SMALL: EquipmentKey[] = [
  ...GARAGE,
  "ez_bar",
  "dip_station",
  "preacher_bench",
  "cable_station",
  "lat_pulldown",
  "seated_row",
  "smith_machine",
  "leg_press",
  "leg_extension",
  "leg_curl",
  "chest_press_machine",
  "calf_raise_machine",
  "exercise_ball",
  "treadmill",
  "stationary_bike",
];

/** What a new gym of each kind starts with. A large gym starts with the whole catalogue. */
export const GYM_PRESETS: Record<GymKind, EquipmentKey[]> = {
  [GymKind.LARGE]: EQUIPMENT_KEYS,
  [GymKind.SMALL]: SMALL,
  [GymKind.GARAGE]: GARAGE,
};
